import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFeatureGate } from '../hooks/useFeatureGate';
import { getUserQRCodes, deleteQRCode } from '../config/firebase';
import Header from '../components/Header';
import Footer from '../components/Footer';

// ── Styled Components ──────────────────────────────────────────
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f9fafb;
`;

const Main = styled.main`
  flex: 1;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  padding: 6rem 1.5rem 4rem;

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 7rem 2rem 6rem;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2.5rem;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const UserMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const UserEmail = styled.span`
  font-size: 0.9rem;
  color: #6b7280;
`;

const SignOutBtn = styled.button`
  padding: 0.4rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #374151;
  font-size: 0.875rem;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover {
    background: #f3f4f6;
  }
`;

const UsageBar = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const UsageText = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  span {
    font-weight: 600;
    color: #000000;
  }
`;

const UpgradeLink = styled(Link)`
  font-size: 0.875rem;
  color: #7c3aed;
  font-weight: 600;
  text-decoration: none;
  margin-left: auto;
  &:hover {
    text-decoration: underline;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
  margin: 0 0 1.25rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: #ffffff;
  border-radius: 1rem;
  border: 1px dashed #d1d5db;

  p {
    color: #6b7280;
    margin: 0.5rem 0 1.5rem;
  }
`;

const CreateBtn = styled(Link)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: #7c3aed;
  color: #ffffff;
  border-radius: 0.5rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease;
  &:hover {
    background: #6d28d9;
  }
`;

const QRGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const QRCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: box-shadow 0.2s ease;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const QRCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.625rem;
  border-radius: 99px;
  background: #ede9fe;
  color: #7c3aed;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 1.125rem;
  line-height: 1;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: color 0.15s ease;
  &:hover {
    color: #ef4444;
  }
`;

const QRContent = styled.p`
  font-size: 0.875rem;
  color: #374151;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QRDate = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: auto;
`;

// ── Helpers ────────────────────────────────────────────────────
const getContentPreview = (qr) => {
  if (qr.type === 'contact') {
    const match = qr.data?.match(/FN:(.+)/);
    return match ? match[1].trim() : 'Contact';
  }
  return (qr.data || '').substring(0, 80);
};

const formatDate = (createdAt) => {
  if (!createdAt) return '';
  const date = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// ── Component ──────────────────────────────────────────────────
const Account = () => {
  const { user, signOut } = useAuth();
  const { maxSavedQRCodes, isPro } = useFeatureGate();
  const navigate = useNavigate();
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getUserQRCodes(user.uid, 50)
      .then(setQrCodes)
      .finally(() => setLoading(false));
  }, [user]);

  const handleDelete = async (id) => {
    if (!user) return;
    await deleteQRCode(user.uid, id);
    setQrCodes((prev) => prev.filter((qr) => qr.id !== id));
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <PageWrapper>
      <Header />
      <Main>
        <PageHeader>
          <PageTitle>My QR Codes</PageTitle>
          <UserMeta>
            <UserEmail>{user?.email}</UserEmail>
            <SignOutBtn onClick={handleSignOut}>Sign Out</SignOutBtn>
          </UserMeta>
        </PageHeader>

        {!isPro && (
          <UsageBar>
            <UsageText>
              <span>{qrCodes.length}</span> of <span>{maxSavedQRCodes}</span>{' '}
              saved QR codes used
            </UsageText>
            <UpgradeLink to="/#pricing">Upgrade to Pro →</UpgradeLink>
          </UsageBar>
        )}

        <SectionTitle>Saved QR Codes</SectionTitle>

        {loading ? (
          <p style={{ color: '#6b7280' }}>Loading…</p>
        ) : qrCodes.length === 0 ? (
          <EmptyState>
            <p>You haven&apos;t saved any QR codes yet.</p>
            <CreateBtn to="/qr">Create Your First QR Code</CreateBtn>
          </EmptyState>
        ) : (
          <QRGrid>
            {qrCodes.map((qr) => (
              <QRCard key={qr.id}>
                <QRCardHeader>
                  <TypeBadge>{qr.type || 'url'}</TypeBadge>
                  <DeleteBtn
                    onClick={() => handleDelete(qr.id)}
                    title="Delete"
                    aria-label="Delete QR code"
                  >
                    ×
                  </DeleteBtn>
                </QRCardHeader>
                <QRContent title={getContentPreview(qr)}>
                  {getContentPreview(qr)}
                </QRContent>
                <QRDate>{formatDate(qr.createdAt)}</QRDate>
              </QRCard>
            ))}
          </QRGrid>
        )}
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default Account;
